import React from 'react';
import { Box, Flex, Grid, GridItem, Spinner, Text } from '@chakra-ui/react';
import { Context } from '../App';
import { SPACING } from '../generic/config';
import Icon, { Icons } from '../base-components/Icons';
import { IconDefinition } from '@fortawesome/free-solid-svg-icons';
import { Bar, Doughnut, Pie } from 'react-chartjs-2';
import { Chart, ArcElement, Tooltip, Legend, Title, BarElement, LinearScale, CategoryScale, PointElement, LineElement } from 'chart.js'
import Card from '../base-components/Card';
import { useQuery } from 'react-query';
import { QueryKeys } from '../api/base/react-query';
import { getDashboardStats } from '../api/dashboard-controller';

Chart.register(ArcElement, Tooltip, Legend, PointElement, LineElement);
Chart.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const COLORS = [
    'rgba(255, 102, 102, 0.4)',  // Red
    'rgba(102, 204, 255, 0.4)',  // Blue
    'rgba(153, 255, 51, 0.4)',   // Lime
    'rgba(255, 102, 255, 0.4)',  // Magenta
    'rgba(255, 153, 51, 0.4)',   // Amber
    'rgba(102, 255, 102, 0.4)',  // Green
    'rgba(153, 102, 255, 0.4)',  // Purple
    'rgba(255, 204, 102, 0.4)',  // Orange
    'rgba(0, 204, 204, 0.4)',    // Cyan
    'rgba(204, 102, 255, 0.4)'   // Violet
];

const DashboardPage: React.FC = () => {
    const { translations } = React.useContext(Context);
    const { data, isLoading } = useQuery(QueryKeys.dashboard, getDashboardStats);

    if (isLoading || !data) {
        return <Flex justify="center" align="center" h="200px"><Spinner size="xl" /></Flex>;
    }

    const statusLabels = Object.keys(data.ordersByStatus);
    const statusValues = Object.values(data.ordersByStatus);

    const statusChartData = {
        labels: statusLabels,
        datasets: [{
            label: '# orders',
            data: statusValues,
            backgroundColor: COLORS.slice(0, statusLabels.length),
            borderColor: COLORS.slice(0, statusLabels.length),
            borderWidth: 1,
        }],
    };

    const resourceLabels = data.completedPerResource.map(r => r.resourceCode);
    const resourceValues = data.completedPerResource.map(r => r.count);

    const resourceChartData = {
        labels: resourceLabels,
        datasets: [{
            label: '# completed orders',
            data: resourceValues,
            backgroundColor: COLORS.slice(0, resourceLabels.length),
            borderColor: COLORS.slice(0, resourceLabels.length),
            borderWidth: 1,
        }],
    };

    const statusCount = (status: string) => String(data.ordersByStatus[status] ?? 0);

    return (
        <Grid templateColumns="repeat(12, 1fr)" gap={SPACING * 4} m={SPACING * 2}>
            <StatCard title={translations.dashboard.totalWorksites} subtitle={translations.dashboard.totalWorksitesSubtitle} value={String(data.totalWorksites)} icon={Icons.Worksite} />
            <StatCard title={translations.dashboard.totalCustomers} subtitle={translations.dashboard.totalCustomersSubtitle} value={String(data.totalCustomers)} icon={Icons.Customer} />
            <StatCard title={translations.dashboard.totalOrders} subtitle={translations.dashboard.totalOrdersSubtitle} value={String(data.totalOrders)} icon={Icons.Order} />
            <StatCard title={translations.dashboard.completedOrders} subtitle={translations.dashboard.completedOrdersSubtitle} value={statusCount("COMPLETED")} icon={Icons.Task} />

            <GridItem colSpan={4}>
                <Card p={SPACING / 2} m={SPACING / 2}>
                    <Pie data={statusChartData} />
                    <Text>{translations.dashboard.ordersByStatus.title}</Text>
                    <Text fontSize="small">{translations.dashboard.ordersByStatus.description}</Text>
                </Card>
            </GridItem>
            <GridItem colSpan={4}>
                <Card p={SPACING / 2} m={SPACING / 2}>
                    <Bar data={resourceChartData} />
                    <Text>{translations.dashboard.completedJobsPerAgent.title}</Text>
                    <Text fontSize="small">{translations.dashboard.completedJobsPerAgent.description}</Text>
                </Card>
            </GridItem>
            <GridItem colSpan={4}>
                <Card p={SPACING / 2} m={SPACING / 2}>
                    <Doughnut data={statusChartData} />
                    <Text>{translations.dashboard.ordersByStatus.title}</Text>
                    <Text fontSize="small">{translations.dashboard.ordersByStatus.description}</Text>
                </Card>
            </GridItem>

            <StatCard title={translations.dashboard.createdOrders} subtitle={translations.dashboard.createdOrdersSubtitle} value={statusCount("CREATED")} icon={Icons.Order} />
            <StatCard title={translations.dashboard.offeredOrders} subtitle={translations.dashboard.offeredOrdersSubtitle} value={statusCount("OFFERED")} icon={Icons.Chart} />
            <StatCard title={translations.dashboard.invoicedOrders} subtitle={translations.dashboard.invoicedOrdersSubtitle} value={statusCount("INVOICED")} icon={Icons.Settings} />
            <StatCard title={translations.dashboard.cancelledOrders} subtitle={translations.dashboard.cancelledOrdersSubtitle} value={statusCount("CANCELLED")} icon={Icons.Close} />
        </Grid>
    );
};

export default DashboardPage;

const StatCard = ({ title, subtitle, value, icon }: { title: string, subtitle: string, value: string, icon: IconDefinition }) =>
(
    <GridItem colSpan={3}>
        <Card p={SPACING / 2} px={SPACING} m={SPACING / 2}>
            <Flex justifyContent={"space-between"}>
                <Box textAlign={"left"}>
                    <Text>{title}</Text>
                    <Text fontSize="small">{subtitle}</Text>
                    <Text>{value}</Text>
                </Box>
                <Box>
                    <Icon icon={icon} size="3x" opacity="40%" />
                </Box>
            </Flex>
        </Card>
    </GridItem>
);
